import React, { Fragment } from 'react';
import cn from 'classnames';

import { useEnum } from '../../hooks/useEnum';
import { mapAttributesToValueLabelPairs, sortByAlphabet } from '../../model/Enum';
import type { Country } from '../../services/Country';

import Radio, { RadioField } from './Radio';

type Props = {
  value: Country | '';
  onChange(value: Country): void;
  className?: string;
  inline?: boolean;
};

export function CountryRadioGroup({ value, onChange }: Props) {
  const [countries] = useEnum('country');
  const options = mapAttributesToValueLabelPairs(countries);

  return (
    <>
      {sortByAlphabet(options).map(option => (
        <Fragment key={option.value}>
          <input
            className="visually-hidden"
            id={`input-country-${option.value}`}
            name="input-country"
            onChange={e => onChange(e.target.value as Country)}
            type="radio"
            value={option.value}
          />
          <label
            className={`list__check--item ${option.value === value ? 'is-active' : ''}`}
            htmlFor={`input-country-${option.value}`}
          >
            <span className="list__check--name">
              <i className={`mr-2 flag flag-${option.value}`} />
              {option.label}
            </span>
            <span className="list__check--checked">
              <i className="icon icon-check" />
            </span>
          </label>
        </Fragment>
      ))}
    </>
  );
}

export function RegionRadioGroup({ value, onChange, ...props }: Props) {
  const [regions] = useEnum('regions');
  const options = mapAttributesToValueLabelPairs(regions);

  return (
    <div className={cn(props.className, { 'form-inline': props.inline })}>
      {sortByAlphabet(options).map((option, index) => (
        <Radio
          checked={value === option.value}
          className={index && !props.inline ? 'mt-2' : null}
          id={`region-${option.value}`}
          key={option.value}
          name="region"
          onChange={event => onChange(event.target.value as Country)}
          value={option.value}
        >
          <i className={`flag flag-${option.value.toLowerCase()} mr-3`} />
          <span>{option.label}</span>
        </Radio>
      ))}
    </div>
  );
}

interface RegionFieldProps {
  name: string;
  inline?: boolean;
  className?: string;
}

export function RegionRadioField(props: RegionFieldProps) {
  const [regions] = useEnum('regions');
  const options = mapAttributesToValueLabelPairs(regions);

  return (
    <div className={cn(props.className, { 'form-inline': props.inline })}>
      {sortByAlphabet(options).map((option, index) => (
        <RadioField
          className={index && !props.inline ? 'mt-2' : null}
          id={`region-${option.value}`}
          key={option.value}
          name={props.name}
          value={option.value}
        >
          <i className={`flag flag-${option.value.toLowerCase()} mr-3`} />
          <span>{option.label}</span>
        </RadioField>
      ))}
    </div>
  );
}
